import React, { useState, useEffect } from 'react'
import CIcon from '@coreui/icons-react'
import { cilChevronBottom, cilChevronTop } from '@coreui/icons'
import BaseListing from 'src/BaseListings'
import { wtGetDateMonthYearFormat } from 'src/helpers/commons/moment'
import { Link } from 'react-router-dom'

export default function EvaluationSummaryReportDataTable({ reportData, reportDataMonths }) {
  const Base = new BaseListing()
  const [isLoading, setIsLoading] = useState(false)

  // keep local state for sorting
  const [tableData, setTableData] = useState([])
  const [sortedKey, setSortedKey] = useState('')
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'asc',
  })

  const DESIGNATION_TYPE_NAMES = {
    1: 'Staff',
    2: 'Worker',
    3: 'Daily Wager'
  };
  const color = ['#4242f5', '#00000', '#f09311', '#eb0920', '#fc03f8', '#00000']

  // when props change, reset local state
  useEffect(() => {
    setTableData(reportData || [])
  }, [reportData])

  const sortTable = (key) => {
    let direction = 'asc'
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc'
    }

    const sorted = [...tableData].sort((a, b) => {
      if (a[key] < b[key]) {
        return direction === 'asc' ? -1 : 1
      }
      if (a[key] > b[key]) {
        return direction === 'asc' ? 1 : -1
      }
      return 0
    })

    setTableData(sorted)
    setSortedKey(key)
    setSortConfig({ key, direction })
  }

  // Helper to convert month-year to Date object
  const parseMonthYear = (monthYear) => {
    const [month, year] = monthYear.split('-')
    return new Date(`${month} 1, ${year}`)
  }

  // Sort the months
  const sortedMonths = [...reportDataMonths].sort((a, b) => {
    return parseMonthYear(a.evaluation_months) - parseMonthYear(b.evaluation_months)
  })

  return (
    <>
      <table className="table table-bordered data-table">
        <thead className="thead-dark border-dark border-1" style={{ backgroundColor: '#e8e9eb' }}>
          <tr>
            <th onClick={() => sortTable('id')}>Sr</th>
            <th onClick={() => sortTable('employee_id')}>
              Id
              {sortedKey === 'employee_id' && (
                <CIcon icon={sortConfig.direction === 'asc' ? cilChevronTop : cilChevronBottom} />
              )}
            </th>
            <th onClick={() => sortTable('full_name')}>
              Full Name
              {sortedKey === 'full_name' && (
                <CIcon icon={sortConfig.direction === 'asc' ? cilChevronTop : cilChevronBottom} />
              )}
            </th>
            <th onClick={() => sortTable('designations')}>
              Designation
              {sortedKey === 'designations' && (
                <CIcon icon={sortConfig.direction === 'asc' ? cilChevronTop : cilChevronBottom} />
              )}
            </th>
            <th onClick={() => sortTable('designations')}>
              Type
              {sortedKey === 'designations' && (
                <CIcon icon={sortConfig.direction === 'asc' ? cilChevronTop : cilChevronBottom} />
              )}
            </th>
            <th onClick={() => sortTable('branches')}>
              Branch
              {sortedKey === 'branches' && (
                <CIcon icon={sortConfig.direction === 'asc' ? cilChevronTop : cilChevronBottom} />
              )}
            </th>
            <th onClick={() => sortTable('departments')}>
              Department
              {sortedKey === 'departments' && (
                <CIcon icon={sortConfig.direction === 'asc' ? cilChevronTop : cilChevronBottom} />
              )}
            </th>
            <th onClick={() => sortTable('date_of_joining')}>
              Date Of Joining
              {sortedKey === 'date_of_joining' && (
                <CIcon icon={sortConfig.direction === 'asc' ? cilChevronTop : cilChevronBottom} />
              )}
            </th>
            {sortedMonths.map((items, index) => (
              <th key={index}>{items.evaluation_months}</th>
            ))}
            <th onClick={() => sortTable('total')}>
              Total
              {sortedKey === 'total' && (
                <CIcon icon={sortConfig.direction === 'asc' ? cilChevronTop : cilChevronBottom} />
              )}
            </th>
            <th onClick={() => sortTable('total')}>
              Percentage
              {sortedKey === 'total' && (
                <CIcon icon={sortConfig.direction === 'asc' ? cilChevronTop : cilChevronBottom} />
              )}
            </th>
          </tr>
        </thead>
        <tbody>
          {tableData.map((item, index) => {
            let total = 0
            let maxTotal = 0

            // Ensure evaluations are sorted in the same order as sortedMonths
            const sortedEvaluations = sortedMonths.map((monthItem) =>
              item.evaluations.find((evalItem) => evalItem.evaluation_month === monthItem.evaluation_months)
            )

            sortedEvaluations.forEach((evaluation) => {
              if (evaluation && evaluation.rating !== '') {
                total += parseInt(evaluation.rating)
                maxTotal += parseInt(evaluation.max_rating)
              }
            })

            const percent = maxTotal ? (total / maxTotal) * 100 : 0

            return (
              <tr key={index}>
                <td className="border-1">{index + 1}</td>
                <td className="border-1">
                  <Link to={`/employee-profile?id=${item.id}`} target="_blank">
                    {item.id}
                  </Link>
                </td>
                <td className="border-1">
                  <Link to={`/employee-profile?id=${item.id}`} target="_blank">
                    {item.name}
                  </Link>
                </td>
                <td className="border-1">{item.designation.name}</td>
                <td>
                  {item.designation.is_officer === 1
                    ? 'Officer'
                    : DESIGNATION_TYPE_NAMES[item.designation.type]
                  }
                </td>
                <td className="border-1">{item.branch.name}</td>
                <td className="border-1">{item.department.name}</td>
                <td className="border-1">{wtGetDateMonthYearFormat(item.date_of_joining)}</td>

                {sortedEvaluations.map((evaluation, i) => (
                  <td
                    key={i}
                    style={{ color: color[i % color.length], fontWeight: 'bold' }}
                    className="border-1"
                  >
                    {!evaluation || evaluation.status === 'NA'
                      ? 'NA'
                      : evaluation.rating > 0
                        ? `${evaluation.rating}/${evaluation.max_rating}`
                        : '-'}
                  </td>
                ))}

                <td className="border-1" style={{ fontWeight: 'bold' }}>
                  {total ? `${total} / ${maxTotal}` : '-'}
                </td>
                <td className="border-1" style={{ fontWeight: 'bold' }}>
                  {percent ? `${percent.toFixed(2)}%` : '-'}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </>
  )
}
